import { useRef, useEffect } from 'react'
import { Printer } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Button } from '@/app/components/ui/button'
import { CartItem, DeliveryDetails } from '@/app/services/pos.service'

export interface TicketPreviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  ticketNumber: string
  branchName?: string
  cashRegisterName?: string
  cashierName: string
  customerType: 'general' | 'registered'
  selectedCustomer: number | null
  customers: { id: number; name: string }[]
  saleType: 'regular' | 'delivery'
  deliveryDetails: DeliveryDetails
  cart: CartItem[]
  subtotal: number
  discountTotal?: number
  tax: number
  deliveryCost: number
  pointsDiscount: number
  total: number
  pointsEarned: number
  pointsToUse: number
  selectedPaymentMethod: string | null
  selectedBank: string
  reference: string
  cashReceived: string
  change: number
  requiresInvoice: boolean
  ticketCopies: number
  completeSale: () => void
  setShowPaymentModal: (show: boolean) => void
  isPrintOnly?: boolean
}

export function TicketPreviewModal({
  open,
  onOpenChange,
  ticketNumber,
  branchName,
  cashRegisterName,
  cashierName,
  customerType,
  selectedCustomer,
  customers,
  saleType,
  deliveryDetails,
  cart,
  subtotal,
  discountTotal,
  tax,
  deliveryCost,
  pointsDiscount,
  total,
  pointsEarned,
  pointsToUse,
  selectedPaymentMethod,
  selectedBank,
  reference,
  cashReceived,
  change,
  requiresInvoice,
  ticketCopies,
  completeSale,
  setShowPaymentModal,
  isPrintOnly = false,
}: TicketPreviewModalProps) {
  const ticketRef = useRef<HTMLDivElement>(null)

  const printTicket = () => {
    const ticketEl = ticketRef.current
    if (!ticketEl) return

    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.top = '-9999px'
    iframe.style.left = '-9999px'
    iframe.style.width = '80mm'
    iframe.style.height = '0'
    iframe.style.border = 'none'
    document.body.appendChild(iframe)

    const doc = iframe.contentDocument ?? iframe.contentWindow?.document
    if (!doc) {
      document.body.removeChild(iframe)
      return
    }

    doc.open()
    const copiesHTML = Array.from({ length: ticketCopies || 1 })
      .map(() => `<div>${ticketEl.innerHTML}</div>`)
      .join('<div style="page-break-after: always;"></div>')

    doc.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Ticket ${ticketNumber}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    @page {
      size: 80mm auto;
      margin: 4mm 3mm;
    }

    body {
      font-family: 'Courier New', Courier, monospace;
      font-size: 11px;
      color: #000;
      width: 74mm;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .text-center  { text-align: center; }
    .font-bold    { font-weight: 700; }
    .text-xs      { font-size: 10px; }
    .text-sm      { font-size: 11px; }
    .text-base    { font-size: 13px; }
    .mb-2         { margin-bottom: 4px; }
    .mb-4         { margin-bottom: 8px; }
    .mt-1         { margin-top: 2px; }
    .mt-2         { margin-top: 4px; }
    .mt-4         { margin-top: 8px; }
    .pt-1         { padding-top: 2px; }
    .pt-2         { padding-top: 4px; }
    .space-y-1 > * + * { margin-top: 2px; }

    .border-t          { border-top: 1px solid #000; }
    .border-dashed     { border-top-style: dashed; }
    .border-slate-300  { border-color: #9ca3af; }

    .flex            { display: flex; }
    .justify-between { justify-content: space-between; }
    .uppercase       { text-transform: uppercase; }

    .text-orange-600 { color: #ea580c; }
    .text-purple-600 { color: #9333ea; }
    .text-slate-500  { color: #64748b; }

    /* El ticket sin borde decorativo */
    .rounded-lg.border-2.border-dashed {
      border: none !important;
      padding: 0 !important;
    }
  </style>
</head>
<body>
  ${copiesHTML}
</body>
</html>`)
    doc.close()

    iframe.onload = () => {
      iframe.contentWindow?.focus()
      iframe.contentWindow?.print()
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe)
        }
      }, 1500)
    }
  }

  const handlePrintAndComplete = () => {
    printTicket()
    completeSale()
  }

  useEffect(() => {
    if (isPrintOnly && open) {
      const timer1 = setTimeout(() => printTicket(), 100)
      const timer2 = setTimeout(() => completeSale(), 1000)
      return () => {
        clearTimeout(timer1)
        clearTimeout(timer2)
      }
    }
  }, [isPrintOnly, open])

  const ticketHtml = (
    <div
      ref={ticketRef}
      className={isPrintOnly ? '' : "rounded-lg border-2 border-dashed border-slate-300 bg-white p-6 font-mono text-sm"}
    >
      <div className="mb-4 text-center">
        <h3 className="text-base font-bold">Farmacias Salud Plus</h3>
        {branchName && <p className="text-xs">Sucursal: {branchName}</p>}
      </div>

      <div className="mb-3 border-t border-dashed border-slate-300 pt-2">
        <p className="text-xs">Ticket: {ticketNumber}</p>
        <p className="text-xs">Caja: {cashRegisterName || '—'}</p>
        <p className="text-xs">Cajero: {cashierName || '—'}</p>
        <p className="text-xs">Fecha: {new Date().toLocaleString()}</p>
        {customerType === 'registered' && selectedCustomer && (
          <p className="text-xs">
            Cliente:{' '}
            {customers.find((c) => c.id === selectedCustomer)?.name}
          </p>
        )}
        {saleType === 'delivery' && (deliveryDetails as any).deliveryPerson && (
          <>
            <p className="mt-1 text-xs font-bold">
              ENTREGA A DOMICILIO
            </p>
            <p className="text-xs">
              Repartidor: {(deliveryDetails as any).deliveryPerson}
            </p>
            <p className="text-xs">
              Tel: {deliveryDetails.customerPhone}
            </p>
            <p className="text-xs">
              Dir: {deliveryDetails.customerAddress}
            </p>
            {deliveryDetails.observations && (
              <p className="text-xs">
                Obs: {deliveryDetails.observations}
              </p>
            )}
          </>
        )}
      </div>

      <div className="mb-2 border-t border-dashed border-slate-300 pt-2">
        {cart.map((item: any) => {
          const regularPrice = parseFloat(String(item.amounts?.regular_unit_total || item.price || item.unit_price || '0').replace(/[^0-9.-]+/g, ""))
          const appliedPrice = parseFloat(String(item.amounts?.applied_unit_total || item.price || item.unit_price || '0').replace(/[^0-9.-]+/g, ""))
          
          const discountPercent = Number(item.promotionDiscount || item.pricing?.discount_percentage || 0)
          const hasDiscount = discountPercent > 0 || regularPrice > appliedPrice

          return (
            <div key={item.id} className="mb-2">
              <p className="text-xs font-bold">{item.name}</p>
              <div className="flex justify-between text-xs">
                <span>
                  {item.quantity} x ${regularPrice.toFixed(2)}
                </span>
                <span>${(regularPrice * item.quantity).toFixed(2)}</span>
              </div>
              {hasDiscount && (
                <div className="flex justify-between text-xs text-orange-600">
                  <span>Desc. {discountPercent > 0 ? `${discountPercent}%` : 'aplicado'}:</span>
                  <span>-${((regularPrice - appliedPrice) * item.quantity).toFixed(2)}</span>
                </div>
              )}
              {item.promotionExpiry && hasDiscount && (
                <p className="text-xs text-slate-500">
                  Válido:{' '}
                  {new Date(
                    item.promotionExpiry,
                  ).toLocaleDateString('es-MX')}
                </p>
              )}
              {item.promotion && (
                <p className="text-xs text-purple-600 mt-1">
                  {item.promotion}
                </p>
              )}
            </div>
          )
        })}
      </div>

      <div className="space-y-1 border-t border-dashed border-slate-300 pt-2">
        <div className="flex justify-between text-xs">
          <span>Subtotal:</span>
          <span>${subtotal.toFixed(2)}</span>
        </div>
        {discountTotal !== undefined && discountTotal > 0 && (
          <div className="flex justify-between text-xs text-orange-600">
            <span>Descuentos:</span>
            <span>-${discountTotal.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between text-xs">
          <span>IVA (16%):</span>
          <span>${tax.toFixed(2)}</span>
        </div>
        {saleType === 'delivery' && deliveryCost > 0 && (
          <div className="flex justify-between text-xs">
            <span>Costo de Envío:</span>
            <span>${deliveryCost.toFixed(2)}</span>
          </div>
        )}
        {pointsDiscount > 0 && (
          <div className="flex justify-between text-xs">
            <span>Desc. Puntos:</span>
            <span>-${pointsDiscount.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between border-t border-slate-300 pt-1 text-sm font-bold">
          <span>TOTAL:</span>
          <span>${total.toFixed(2)}</span>
        </div>
        {customerType === 'registered' && selectedCustomer && (
          <div className="mt-2 border-t border-slate-300 pt-2 text-xs">
            <div className="flex justify-between">
              <span>Puntos ganados:</span>
              <span className="font-bold">+{pointsEarned} pts</span>
            </div>
            {pointsDiscount > 0 && (
              <div className="flex justify-between">
                <span>Puntos usados:</span>
                <span className="font-bold">-{pointsToUse} pts</span>
              </div>
            )}
          </div>
        )}

        {(() => {
          const normalizedPaymentMethod = selectedPaymentMethod?.toLowerCase() || 'cash'
          const isCash = normalizedPaymentMethod === 'cash' || normalizedPaymentMethod === 'efectivo'
          const isCard = normalizedPaymentMethod === 'card' || normalizedPaymentMethod === 'tarjeta'
          const isCredit = normalizedPaymentMethod === 'credit' || normalizedPaymentMethod === 'crédito' || normalizedPaymentMethod === 'credito'
          const isTransfer = normalizedPaymentMethod === 'transfer' || normalizedPaymentMethod === 'transferencia'

          const displayPaymentMethod = isCash ? 'EFECTIVO'
            : isCard ? 'TARJETA DE DÉBITO'
            : isTransfer ? 'TRANSFERENCIA'
            : isCredit ? 'TARJETA DE CRÉDITO'
            : selectedPaymentMethod
          const bank = selectedBank

          return (
            <>
              <div className="mt-2 flex justify-between text-xs">
                <span>Método de pago:</span>
                <span className="uppercase">{displayPaymentMethod}</span>
              </div>

              {(isCard || isCredit || isTransfer) && bank && (
                <div className="flex justify-between text-xs">
                  <span>Banco:</span>
                  <span>{bank}</span>
                </div>
              )}

              {(isCard || isCredit || isTransfer) && reference && (
                <div className="flex justify-between text-xs">
                  <span>Referencia:</span>
                  <span>{reference}</span>
                </div>
              )}

              {isCash && (
                <>
                  <div className="flex justify-between text-xs">
                    <span>Efectivo:</span>
                    <span>${parseFloat(cashReceived || '0').toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span>Cambio:</span>
                    <span>${Number(change || 0).toFixed(2)}</span>
                  </div>
                </>
              )}

            </>
          )
        })()}
      </div>

      <div className="mt-4 text-center text-xs">
        <p>¡Gracias por su compra!</p>
        {requiresInvoice && (
          <p className="mt-1 font-bold">REQUIERE FACTURA</p>
        )}
      </div>
    </div>
  )

  if (isPrintOnly) {
    return <div className="hidden">{open && ticketHtml}</div>
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-sm overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Vista Previa del Ticket</DialogTitle>
          <DialogDescription className="sr-only">
            Verifica los datos del ticket antes de imprimir
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {ticketHtml}
          
          <div className="flex gap-2">
            <Button onClick={handlePrintAndComplete} className="flex-1">
              <Printer className="mr-2 h-4 w-4" />
              Imprimir y Finalizar
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                onOpenChange(false)
                setShowPaymentModal(true)
              }}
            >
              Editar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
